import React, { useState, useEffect, useRef } from 'react';
import { Plus, Loader2, Trash2, Target, X, PlusCircle, Pencil } from 'lucide-react';
import { EmptyState } from './AccountsScreen';
import { useAuth } from '../../context/AuthContext';
import { CURRENCIES, formatUnifiedAmount } from '../../utils/currency';

const GOAL_CATEGORIES = ['Emergency Reserve', 'Travel & Lifestyle', 'Asset / Real Estate', 'Debt Elimination'];

const EMPTY_FORM = { name: '', category: GOAL_CATEGORIES[0], targetAmount: '', currency: 'NGN', targetDate: '', savedAmount: '' };

export default function GoalsScreen() {
  const { user } = useAuth();
  const displayCurrency = user?.displayCurrency || 'NGN';

  const [goals, setGoals]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [contributeId, setContributeId] = useState(null);
  const [contributeAmount, setContributeAmount] = useState('');
  const [editingId, setEditingId] = useState(null);   // goalId being edited, or null when adding
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch('/api/personal/goals', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setGoals(data.goals);
    } finally {
      setLoading(false);
    }
  }

  function closeForm() {
    setShowAdd(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  function startAdd() {
    if (showAdd) { closeForm(); return; }
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowAdd(true);
  }

  // Fill the form with an existing goal and switch it to "edit" mode.
  function startEdit(g) {
    setEditingId(g.goalId);
    setForm({
      name:         g.name || '',
      category:     GOAL_CATEGORIES.includes(g.category) ? g.category : GOAL_CATEGORIES[0],
      targetAmount: String(g.targetAmount ?? ''),
      currency:     g.currency || 'NGN',
      targetDate:   g.targetDate || '',
      savedAmount:  String(g.savedAmount ?? 0),
    });
    setError('');
    setShowAdd(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const editing = Boolean(editingId);
    if (!form.name.trim() || !form.targetAmount || !form.targetDate) { setError('Name, target amount, and target date are required.'); return; }
    if (!(Number(form.targetAmount) > 0)) { setError('Target amount must be greater than zero.'); return; }
    if (editing && (form.savedAmount === '' || Number(form.savedAmount) < 0)) { setError('Saved amount must be zero or more.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(editing ? `/api/personal/goals/${editingId}` : '/api/personal/goals', {
        method: editing ? 'PATCH' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing
          ? { ...form, targetAmount: Number(form.targetAmount), savedAmount: Number(form.savedAmount) }
          : { name: form.name, category: form.category, targetAmount: Number(form.targetAmount), currency: form.currency, targetDate: form.targetDate }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.message || (editing ? 'Could not save changes.' : 'Could not add goal.')); return; }
      closeForm();
      load();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleContribute(id) {
    if (!contributeAmount) return;
    await fetch(`/api/personal/goals/${id}/contribute`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: Number(contributeAmount) }),
    });
    setContributeId(null);
    setContributeAmount('');
    load();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this goal?')) return;
    await fetch(`/api/personal/goals/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Goals</p>
          <h2 className="text-2xl font-black text-zinc-900">What You're Saving For</h2>
        </div>
        <button onClick={startAdd}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 text-black rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-400 transition">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? 'Cancel' : 'Add Goal'}
        </button>
      </div>

      {showAdd && (
        <form ref={formRef} onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-2xl p-5 mb-6 space-y-3">
          {editingId && (
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Editing goal</p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <input placeholder="Goal name (e.g. Emergency Fund)" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500">
              {GOAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Target amount" value={form.targetAmount}
              onChange={e => setForm({ ...form, targetAmount: e.target.value })}
              className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
              className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="date" value={form.targetDate}
              onChange={e => setForm({ ...form, targetDate: e.target.value })}
              className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
            {editingId && (
              <input type="number" placeholder="Saved so far" value={form.savedAmount}
                onChange={e => setForm({ ...form, savedAmount: e.target.value })}
                className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
            )}
          </div>
          {editingId && (
            <p className="text-[11px] text-zinc-500">
              "Saved so far" replaces the current figure — use it to correct a contribution. Changing the currency does not convert the amounts.
            </p>
          )}
          <button type="submit" disabled={submitting}
            className="w-full py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-zinc-800 transition disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="inline animate-spin" /> : (editingId ? 'Save Changes' : 'Save Goal')}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</p>
      ) : goals.length === 0 ? (
        <EmptyState icon={Target} text="No goals yet. Set a savings target — an emergency fund, a trip, a down payment." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {goals.map(g => {
            const pct = Math.min(100, Math.round((g.savedAmount / g.targetAmount) * 100));
            return (
              <div key={g.goalId} className="bg-white border border-zinc-200 rounded-2xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-zinc-400">{g.category} · by {g.targetDate}</p>
                    <p className="font-bold text-zinc-900">{g.name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => startEdit(g)} aria-label="Edit goal" title="Edit"
                      className="text-zinc-300 hover:text-amber-600 transition">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => handleDelete(g.goalId)} aria-label="Delete goal" title="Delete"
                      className="text-zinc-300 hover:text-red-500 transition">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="w-full h-2 bg-zinc-100 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-zinc-500 mt-1.5">
                  {formatUnifiedAmount(g.savedAmount, g.currency, displayCurrency)} of {formatUnifiedAmount(g.targetAmount, g.currency, displayCurrency)} ({pct}%)
                </p>

                {contributeId === g.goalId ? (
                  <div className="flex gap-2 mt-3">
                    <input type="number" autoFocus placeholder="Amount" value={contributeAmount}
                      onChange={e => setContributeAmount(e.target.value)}
                      className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
                    <button onClick={() => handleContribute(g.goalId)} className="px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-bold">Add</button>
                    <button onClick={() => setContributeId(null)} className="px-2 text-zinc-400"><X size={14} /></button>
                  </div>
                ) : (
                  <button onClick={() => setContributeId(g.goalId)}
                    className="flex items-center gap-1 text-xs font-bold text-amber-600 mt-3 hover:text-amber-700">
                    <PlusCircle size={13} /> Add contribution
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
